import { useMemo, useState } from 'react';
import { Check, Copy, Loader2, Mail, MessageCircle, Share2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Toast } from '@/components/ui/toast';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import { buildVariants, type ShareMode, type ShareVariant } from './variants';

/**
 * Story-Output bottom-sheet for sharing the currently-rendered story.
 *
 * Two-phase interaction inside one sheet:
 *   - Phase 1 (default): title + subtitle + variant-cards. Tapping a
 *     card promotes it into the "selected" visual state; non-selected
 *     cards dim to opacity-50.
 *   - Phase 2 (variant selected): a channel row appears below the card
 *     list with 4 outbound channel buttons. Tapping a channel fires the
 *     corresponding side-effect and closes the sheet.
 *
 * Loading + error surfaces (Gate-1 D2 addendum): callers may open the
 * sheet before story data is available (Now-Playing entry point fetches
 * on demand). While `loading` is true, the variant-card list is
 * replaced with a centered spinner + "Wird geladen…" label. If
 * `loadError` is set, the sheet closes immediately and the caller is
 * expected to surface the error via its own toast — this component
 * only renders the sheet surface.
 *
 * `open` is controlled by the parent. Selection state is internal and
 * resets on close so the next open re-enters Phase 1.
 *
 * Mode-awareness: the variant set comes from `buildVariants(childName,
 * storyTitle, mode)` — resolved by the caller via `resolveStoryMode`.
 * Sheet title switches on the same prop.
 */
interface ShareBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When true, variant-cards are replaced by a spinner + loading label. */
  loading?: boolean;
  /** Present only when data has been resolved; required for Phase 1. */
  childName: string | null;
  storyTitle: string;
  mode: ShareMode;
}

type ChannelKey = 'whatsapp' | 'email' | 'copy' | 'moreApps';

const TOAST_DURATION_MS = 2000;

export function ShareBottomSheet({
  open,
  onOpenChange,
  loading,
  childName,
  storyTitle,
  mode,
}: ShareBottomSheetProps) {
  const variants = useMemo(
    () => buildVariants(childName, storyTitle, mode),
    [childName, storyTitle, mode],
  );

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);

  // Reset interaction state whenever the sheet closes — the next open
  // should re-enter Phase 1 without a stale selection. React 19
  // store-prev-prop pattern (no useEffect) keeps this synchronous and
  // satisfies react-hooks/set-state-in-effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setSelectedKey(null);
      setToastVisible(false);
    }
  }

  const copy = STRINGS.storyOutput.page.share.sheet;
  const title = mode === 'sb_explanation' ? copy.titleSbExplanation : copy.titleStandard;

  const selectedVariant = variants.find((v) => v.key === selectedKey) ?? null;

  const handleChannelClick = (channel: ChannelKey) => {
    if (!selectedVariant) return;

    if (channel === 'whatsapp') {
      window.open(
        'https://wa.me/?text=' + encodeURIComponent(selectedVariant.whatsappText),
        '_blank',
        'noopener,noreferrer',
      );
      onOpenChange(false);
      return;
    }

    if (channel === 'email') {
      const href =
        'mailto:?subject=' +
        encodeURIComponent(selectedVariant.emailSubject) +
        '&body=' +
        encodeURIComponent(selectedVariant.emailBody);
      window.location.href = href;
      onOpenChange(false);
      return;
    }

    if (channel === 'copy') {
      void copyToClipboard(selectedVariant.copyUrl).finally(() => {
        setToastVisible(true);
        window.setTimeout(() => {
          setToastVisible(false);
          onOpenChange(false);
        }, TOAST_DURATION_MS);
      });
      return;
    }

    // moreApps — Web Share API when available, else fallback to copy.
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      void navigator.share({ text: selectedVariant.whatsappText }).catch(() => {
        // User cancelled or share failed — silent.
      });
      onOpenChange(false);
      return;
    }
    void copyToClipboard(selectedVariant.copyUrl).finally(() => {
      setToastVisible(true);
      window.setTimeout(() => {
        setToastVisible(false);
        onOpenChange(false);
      }, TOAST_DURATION_MS);
    });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="gap-1 px-6 pt-6">
            <SheetTitle className="text-base font-semibold text-dream-navy">{title}</SheetTitle>
            <SheetDescription className="text-sm text-dream-navy/60">
              {copy.subtitle}
            </SheetDescription>
          </SheetHeader>

          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12">
              <Loader2 className="h-6 w-6 animate-spin text-dream-navy" aria-hidden="true" />
              <p className="text-sm text-dream-navy/70">{copy.loading}</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 px-6 pt-2">
                {variants.map((v) => (
                  <VariantCard
                    key={v.key}
                    variant={v}
                    selected={selectedKey === v.key}
                    hasSelection={selectedKey !== null}
                    onSelect={() => setSelectedKey(v.key)}
                  />
                ))}
              </div>

              {selectedVariant && (
                <div className="mt-4 border-t border-dream-navy/10 px-6 pt-4 pb-6">
                  <p className="mb-3 text-xs font-medium text-dream-navy/70">{copy.sendeUeber}</p>
                  <div className="grid grid-cols-4 gap-2">
                    <ChannelButton
                      icon={<MessageCircle className="h-5 w-5" strokeWidth={2} />}
                      label={copy.channels.whatsapp}
                      onClick={() => handleChannelClick('whatsapp')}
                    />
                    <ChannelButton
                      icon={<Mail className="h-5 w-5" strokeWidth={2} />}
                      label={copy.channels.email}
                      onClick={() => handleChannelClick('email')}
                    />
                    <ChannelButton
                      icon={<Copy className="h-5 w-5" strokeWidth={2} />}
                      label={copy.channels.copy}
                      onClick={() => handleChannelClick('copy')}
                    />
                    <ChannelButton
                      icon={<Share2 className="h-5 w-5" strokeWidth={2} />}
                      label={copy.channels.moreApps}
                      onClick={() => handleChannelClick('moreApps')}
                      variant="primary"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <Toast message={copy.toast} visible={toastVisible} />
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────

interface VariantCardProps {
  variant: ShareVariant;
  selected: boolean;
  hasSelection: boolean;
  onSelect: () => void;
}

function VariantCard({ variant, selected, hasSelection, onSelect }: VariantCardProps) {
  const preview = truncatePreview(variant.whatsappText, 80);
  const ariaSelected = STRINGS.storyOutput.page.share.sheet.ariaLabel.variantSelected;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl border bg-dream-background/50 p-3 text-left transition-all',
        selected
          ? 'border-dream-navy bg-dream-navy/5'
          : 'border-dream-navy/10 hover:border-dream-navy/30',
        hasSelection && !selected && 'opacity-50',
      )}
    >
      <span className="text-xl leading-none" aria-hidden="true">
        {variant.emoji}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-dream-navy">{variant.label}</span>
          {selected && (
            <Check
              className="h-4 w-4 text-dream-navy"
              strokeWidth={2.5}
              aria-label={ariaSelected}
            />
          )}
        </span>
        <span className="mt-1 block text-xs leading-snug text-dream-navy/60">{preview}</span>
      </span>
    </button>
  );
}

interface ChannelButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'primary';
}

function ChannelButton({ icon, label, onClick, variant = 'default' }: ChannelButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition-colors',
        variant === 'primary'
          ? 'border-dream-navy bg-dream-navy text-dream-white hover:bg-dream-navy/90'
          : 'border-dream-navy/10 bg-dream-background/50 text-dream-navy hover:border-dream-navy/30',
      )}
    >
      {icon}
      <span className="text-center leading-tight">{label}</span>
    </button>
  );
}

// ── Helpers ────────────────────────────────────────────────────

/**
 * Truncate a WhatsApp-style message for use as a small preview inside
 * a variant card. Prefers the last word boundary within the limit so
 * the preview doesn't cut a word mid-character. Strips trailing
 * newlines + leading URL lines first — the preview is a teaser, not
 * the full payload. Uses hard ceiling of `max + 20` to bound lookups
 * on pathological inputs.
 */
function truncatePreview(text: string, max: number): string {
  const firstLine = text.split('\n')[0] ?? '';
  if (firstLine.length <= max) return firstLine;
  const hardSlice = firstLine.slice(0, max);
  const lastSpace = hardSlice.lastIndexOf(' ');
  const cut = lastSpace > max / 2 ? hardSlice.slice(0, lastSpace) : hardSlice;
  return cut + '…';
}

async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to execCommand fallback.
    }
  }
  if (typeof document === 'undefined') return;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.top = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}
